function clearPendingProductSections() {
  document.querySelectorAll('.missence-pdp-section.is-pending').forEach((section) => {
    section.classList.remove('is-pending');
  });
}

function getMissenceClaimsObjectives(root) {
  return Array.from(root.querySelectorAll('[data-missence-claims-objective]'));
}

function showMissenceClaimsObjective(root, objectiveId, { animate = false, announce = false } = {}) {
  const objectives = getMissenceClaimsObjectives(root);
  const target = objectives.find((objective) => objective.id === objectiveId) || objectives[0];
  if (!target) return;

  const container = root.querySelector('[data-missence-claims-objectives]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const shouldAnimate = animate && !reduceMotion && container;
  const startHeight = shouldAnimate ? container.getBoundingClientRect().height : 0;

  objectives.forEach((objective) => {
    objective.hidden = objective !== target;
  });

  if (announce) {
    const nutrients = Array.from(target.querySelectorAll('.missence-pdp-claims__nutrient'));
    nutrients.forEach((details, index) => {
      details.open = index === 0;
    });

    const status = root.querySelector('[data-missence-claims-status]');
    const title = target.querySelector('.missence-pdp-claims__objective-title');
    if (status && title) status.textContent = title.textContent.trim();
  }

  if (!shouldAnimate) return;

  if (container.__missenceClaimsEndHandler) container.__missenceClaimsEndHandler();
  const endHeight = container.getBoundingClientRect().height;

  target.classList.remove('is-entering');
  void target.offsetWidth;
  target.classList.add('is-entering');
  if (Math.abs(endHeight - startHeight) < 1) return;

  // Sin transitionend (pestaña oculta, transición cortada), el temporizador deja la altura libre igualmente.
  let fallbackTimer = null;
  const endHandler = (event) => {
    if (event && (event.target !== container || event.propertyName !== 'height')) return;
    container.removeEventListener('transitionend', endHandler);
    window.clearTimeout(fallbackTimer);
    container.__missenceClaimsEndHandler = null;
    container.classList.remove('is-resizing');
    container.style.height = '';
  };

  container.__missenceClaimsEndHandler = endHandler;
  container.style.height = `${startHeight}px`;
  container.classList.add('is-resizing');
  void container.offsetHeight;
  container.addEventListener('transitionend', endHandler);
  fallbackTimer = window.setTimeout(endHandler, 500);
  container.style.height = `${endHeight}px`;
}

function initMissenceClaims(scope = document) {
  scope.querySelectorAll('[data-missence-claims]').forEach((root) => {
    if (root.dataset.missenceClaimsReady === 'true') return;

    const selector = root.querySelector('[data-missence-claims-selector]');
    const select = root.querySelector('[data-missence-claims-objective-select]');
    if (!selector || !select || getMissenceClaimsObjectives(root).length < 2) return;

    root.dataset.missenceClaimsReady = 'true';
    selector.hidden = false;
    showMissenceClaimsObjective(root, select.value);
  });
}

function setIngredientModalPanel(modal, panelId) {
  modal.querySelectorAll('[data-missence-ingredient-panel]').forEach((panel) => {
    const isActive = panel.getAttribute('data-missence-ingredient-panel') === panelId;
    panel.hidden = !isActive;
  });
}

function openIngredientModal(root, panelId) {
  const modal = root.querySelector('[data-missence-ingredient-modal]');
  if (!modal) return;

  setIngredientModalPanel(modal, panelId);
  modal.classList.remove('is-closing');
  modal.classList.remove('is-active');
  modal.hidden = false;
  document.body.classList.add('overflow-hidden');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add('is-active');
    });
  });
}

function closeIngredientModal(modal) {
  if (!modal) return;
  modal.classList.remove('is-active');
  modal.classList.add('is-closing');

  const finishClose = () => {
    modal.hidden = true;
    modal.classList.remove('is-closing');
    document.body.classList.remove('overflow-hidden');
  };

  const dialog = modal.querySelector('.missence-pdp-ingredient-modal__dialog');
  if (!dialog) {
    finishClose();
    return;
  }

  dialog.addEventListener('transitionend', finishClose, { once: true });
}

function syncDifferences(root, nextIndex) {
  const track = root.querySelector('[data-missence-differences-track]');
  const pages = Array.from(root.querySelectorAll('[data-missence-differences-page]'));
  const progress = root.querySelector('[data-missence-differences-progress]');
  const prev = root.querySelector('[data-missence-differences-prev]');
  const next = root.querySelector('[data-missence-differences-next]');
  if (!track || !pages.length) return;

  const maxIndex = pages.length - 1;
  const index = Math.max(0, Math.min(nextIndex, maxIndex));

  track.style.transform = `translateX(-${index * 100}%)`;
  root.setAttribute('data-missence-differences-current', String(index));

  pages.forEach((page, pageIndex) => {
    page.classList.toggle('is-active', pageIndex === index);
  });

  if (progress) {
    const visualSteps = pages.length;
    progress.style.width = `${((index + 1) / visualSteps) * 100}%`;
  }

  if (prev) prev.classList.toggle('is-disabled', index === 0);
  if (next) next.classList.toggle('is-disabled', index === maxIndex);
}

// Miniaturas en una sola fila: si no caben ni al mínimo, el ancho se calcula para que asome
// media miniatura al final de la fila y se note que hay más.
function updateMissenceThumbsPeek(row) {
  const thumbs = row.querySelectorAll('[data-missence-gallery-thumb]');
  const styles = window.getComputedStyle(row);
  const gap = parseFloat(styles.columnGap) || 0;
  const min = parseFloat(styles.getPropertyValue('--missence-thumbs-min')) * 10 || 46;
  const width = row.clientWidth - (parseFloat(styles.paddingLeft) || 0) - (parseFloat(styles.paddingRight) || 0);
  const count = thumbs.length;

  if (!count || count * min + (count - 1) * gap <= width + 0.5) {
    row.removeAttribute('data-missence-thumbs-peek');
    row.style.removeProperty('--missence-thumbs-peek-size');
    return;
  }

  // Miniaturas enteras que caben dejando media a la vista: width = k · (size + gap) + size / 2.
  const whole = Math.max(1, Math.floor((width - min / 2) / (min + gap)));
  const size = (width - whole * gap) / (whole + 0.5);
  row.style.setProperty('--missence-thumbs-peek-size', `${size}px`);
  row.setAttribute('data-missence-thumbs-peek', '');
}

function initMissenceThumbsRow(root) {
  const row = root.querySelector('[data-missence-thumbs-row]');
  if (!row || row.dataset.missenceThumbsRowReady === 'true') return;
  row.dataset.missenceThumbsRowReady = 'true';
  updateMissenceThumbsPeek(row);
  new ResizeObserver(() => updateMissenceThumbsPeek(row)).observe(row);
}

// Lleva la miniatura activa a la vista desplazando solo la fila, sin mover la página.
function revealMissenceActiveThumb(root, thumb) {
  const row = thumb.closest('[data-missence-thumbs-row]');
  if (!row || row.scrollWidth <= row.clientWidth) return;

  const padding = parseFloat(window.getComputedStyle(row).paddingLeft) || 0;
  const start = thumb.offsetLeft - padding;
  const end = start + thumb.offsetWidth + padding * 2;
  let left = null;
  if (start < row.scrollLeft) left = start;
  else if (end > row.scrollLeft + row.clientWidth) left = end - row.clientWidth;
  if (left === null) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  row.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
}

function setMissenceGalleryActiveState(root, mediaId) {
  if (!root) return;

  const targetId = String(mediaId);

  root.querySelectorAll('[data-missence-stage]').forEach((stage) => {
    stage.classList.toggle('is-active', stage.getAttribute('data-missence-stage') === targetId);
  });

  root.querySelectorAll('[data-missence-gallery-thumb]').forEach((thumb) => {
    const isActive = thumb.getAttribute('data-missence-gallery-thumb') === targetId;
    thumb.classList.toggle('is-active', isActive);
    if (isActive) {
      thumb.setAttribute('aria-current', 'true');
      revealMissenceActiveThumb(root, thumb);
    } else {
      thumb.removeAttribute('aria-current');
    }
  });
}

function initMissenceProductGallery(root) {
  if (!root || root.dataset.missenceGalleryReady === 'true') return;

  const viewport = root.querySelector('[data-embla-viewport]');
  if (!viewport) return;
  const slides = Array.from(root.querySelectorAll('[data-missence-slide]'));
  if (!slides.length) return;

  root.querySelectorAll('[data-missence-gallery-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const mediaId = thumb.getAttribute('data-missence-gallery-thumb');
      if (typeof root.__missenceScrollToMedia === 'function') {
        root.__missenceScrollToMedia(mediaId);
      } else {
        setMissenceGalleryActiveState(root, mediaId);
      }
    });
  });

  root.dataset.missenceGalleryReady = 'true';
  initMissenceThumbsRow(root);

  const initialActiveSlide =
    root.querySelector('.missence-pdp-main__slide.is-active') ||
    slides[0];

  const startIndex = Math.max(0, slides.indexOf(initialActiveSlide));
  let initAttempts = 0;

  const mountEmbla = () => {
    if (root.__missenceEmbla || root.dataset.missenceGalleryReady === 'done') return;

    if (!window.EmblaCarousel) {
      initAttempts += 1;
      if (initAttempts <= 20) {
        window.setTimeout(mountEmbla, 120);
      }
      return;
    }

    const embla = window.EmblaCarousel(viewport, {
      align: 'start',
      containScroll: 'trimSnaps',
      dragFree: false,
      loop: false,
      skipSnaps: false,
      watchDrag: true,
      startIndex,
    });

    const syncSelectedSlide = () => {
      const selectedIndex = embla.selectedScrollSnap();
      const activeSlide = slides[selectedIndex];
      if (!activeSlide) return;
      setMissenceGalleryActiveState(root, activeSlide.getAttribute('data-missence-stage'));
    };

    root.__missenceScrollToMedia = (mediaId) => {
      const targetIndex = slides.findIndex((slide) => slide.getAttribute('data-missence-stage') === String(mediaId));
      if (targetIndex === -1) return;
      embla.scrollTo(targetIndex);
    };

    embla.on('init', syncSelectedSlide);
    embla.on('reInit', syncSelectedSlide);
    embla.on('select', syncSelectedSlide);
    root.__missenceEmbla = embla;
    root.dataset.missenceGalleryReady = 'done';
    syncSelectedSlide();
  };

  mountEmbla();

}

function syncMissenceSubscriptionCards(root) {
  if (!root) return;

  root.querySelectorAll('[data-missence-subscription-card]').forEach((card) => {
    const radio = card.querySelector('.missence-pdp-main__subscription-radio');
    card.classList.toggle('is-selected', Boolean(radio?.checked));
  });
}

function dispatchMissenceInputChange(input) {
  if (!input) return;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function syncMissenceShopifyAppBlock(root) {
  if (!root) return;

  const productInfo = root.closest('product-info');
  const currentVariantId = productInfo?.querySelector('input.product-variant-id')?.value || '';
  const subscriptionVariantId = root.getAttribute('data-missence-subscription-variant-id') || currentVariantId;
  const checkedOption = root.querySelector('.missence-pdp-main__subscription-radio:checked');
  if (!checkedOption) return;

  const purchaseType = checkedOption.dataset.sellingPlanType || 'subscription';
  const sellingPlanId = checkedOption.value || '';
  const targetVariantId = purchaseType === 'subscription' ? subscriptionVariantId : currentVariantId;
  if (!targetVariantId) return;

  document.querySelectorAll('.shopify_subscriptions_app_block_container').forEach((container) => {
    const targetSection = container.querySelector(`.shopify_subscriptions_app_block[data-variant-id="${targetVariantId}"]`);
    if (!targetSection) return;

    const oneTimeInput = targetSection.querySelector('input[data-radio-type="one_time_purchase"]');
    const sellingPlanGroupInput = targetSection.querySelector('input[data-radio-type="selling_plan_group"]');
    const sellingPlanInput = sellingPlanId
      ? targetSection.querySelector(`input[data-radio-type="selling_plan"][data-selling-plan-id="${sellingPlanId}"]`)
      : null;
    const hiddenInput = container.querySelector('input.selected-selling-plan-id');

    if (purchaseType === 'one-time' || sellingPlanId === '') {
      if (oneTimeInput) {
        oneTimeInput.checked = true;
        dispatchMissenceInputChange(oneTimeInput);
      }

      if (hiddenInput) {
        hiddenInput.value = '';
        dispatchMissenceInputChange(hiddenInput);
      }

      return;
    }

    if (sellingPlanGroupInput) {
      sellingPlanGroupInput.checked = true;
      dispatchMissenceInputChange(sellingPlanGroupInput);
    }

    if (sellingPlanInput) {
      sellingPlanInput.checked = true;
      dispatchMissenceInputChange(sellingPlanInput);
    }

    if (hiddenInput) {
      hiddenInput.value = sellingPlanId;
      dispatchMissenceInputChange(hiddenInput);
    }
  });
}

function syncMissenceSellingPlan(root) {
  if (!root) return;

  const form = root.closest('form');
  const hiddenInput =
    root.querySelector('[data-missence-selling-plan]') ||
    form?.querySelector('[data-missence-selling-plan]') ||
    root.closest('product-info')?.querySelector('[data-missence-selling-plan]');
  const checkedOption = root.querySelector('.missence-pdp-main__subscription-radio:checked');
  if (!checkedOption) return;

  const sellingPlanId = checkedOption.getAttribute('value') || '';
  const type = checkedOption.dataset.sellingPlanType || 'subscription';
  const quantityInput = form?.querySelector('[data-missence-purchase-quantity]');
  const packQuantityInput = form?.querySelector('[data-missence-subscription-pack-quantity]');
  const defaultQuantity = Math.max(1, Number(quantityInput?.dataset.defaultQuantity || 1));
  const subscriptionQuantity = Math.max(1, Number(checkedOption.dataset.subscriptionQuantity || defaultQuantity));

  if (hiddenInput) {
    hiddenInput.value = sellingPlanId;
    hiddenInput.disabled = type === 'one-time' || sellingPlanId === '';
  }

  if (quantityInput) {
    quantityInput.value = String(type === 'subscription' && sellingPlanId !== '' ? subscriptionQuantity : defaultQuantity);
  }

  if (packQuantityInput) {
    packQuantityInput.value = String(subscriptionQuantity);
    packQuantityInput.disabled = type === 'one-time' || sellingPlanId === '';
  }

  syncMissenceSubscriptionCards(root);
  syncMissenceHeaderPrice(root, type);
  syncMissenceShopifyAppBlock(root);
}

function syncMissenceHeaderPrice(root, purchaseMode = null) {
  if (!root) return;

  const productInfo = root.closest('product-info');
  const mainPrice = productInfo?.querySelector('[data-missence-main-price]');
  if (!mainPrice) return;
  const hasCustomSubscriptionUi = root.hasAttribute('data-missence-subscription-variant-id');

  const mode =
    purchaseMode ||
    root.querySelector('.missence-pdp-main__subscription-radio:checked')?.dataset.sellingPlanType ||
    'subscription';

  mainPrice.hidden = hasCustomSubscriptionUi || mode === 'subscription';
  syncMissenceVisibilityContainer(productInfo?.querySelector('[data-missence-price-group]'));
}

function syncMissenceVisibilityContainer(container) {
  if (!container) return;

  const hasVisibleContent = Array.from(container.children).some((child) => {
    if (child.hidden) return false;
    if (window.getComputedStyle(child).display === 'none') return false;
    return child.textContent.trim() !== '';
  });

  container.hidden = !hasVisibleContent;
}

function parseMissenceJsonScript(node) {
  if (!node) return null;

  try {
    return JSON.parse(node.textContent || '');
  } catch (error) {
    return null;
  }
}

function announceMissencePurchaseOption(root) {
  if (!root) return;

  const productInfo = root.closest('product-info');
  const announcer = productInfo?.querySelector('[data-missence-purchase-announcer]');
  const checkedOption = root.querySelector('.missence-pdp-main__subscription-radio:checked');
  if (!announcer || !checkedOption) return;

  // Si el foco está en el propio radio, el lector ya lee su nombre y su precio (aria-describedby): no se repite.
  if (document.activeElement === checkedOption) return;

  const card = checkedOption.closest('[data-missence-subscription-card]');
  const optionData = parseMissenceJsonScript(card?.querySelector('[data-missence-purchase-option-json]'));
  const title = card?.querySelector('.missence-pdp-main__subscription-card-title')?.textContent.trim() || '';
  // Se anuncia el precio que se ve en la tarjeta (con el bloque «Precio por mes y dosis», el precio por mes).
  const price = card?.querySelector('.missence-pdp-main__subscription-card-price')?.textContent.trim() || optionData?.chargeFormatted || '';
  const message = [title, price].filter(Boolean).join(', ');
  if (!message || announcer.dataset.lastMessage === message) return;

  announcer.dataset.lastMessage = message;
  announcer.textContent = '';
  window.setTimeout(() => {
    announcer.textContent = message;
  }, 100);
}

function syncMissenceTabState(root, activeTabKey, activeVariantId = '') {
  if (!root) return;

  root.querySelectorAll('[data-missence-subscription-tab-trigger]').forEach((trigger) => {
    const isActive = activeTabKey !== 'variant' && trigger.getAttribute('data-missence-subscription-tab-trigger') === activeTabKey;
    trigger.classList.toggle('is-active', isActive);
    trigger.setAttribute('aria-pressed', String(isActive));
  });

  root.querySelectorAll('[data-missence-variant-tab-trigger]').forEach((trigger) => {
    const isActive =
      activeTabKey === 'variant' && trigger.getAttribute('data-missence-variant-tab-trigger') === String(activeVariantId);
    trigger.classList.toggle('is-active', isActive);
    trigger.setAttribute('aria-pressed', String(isActive));
  });
}

function syncMissencePurchaseBlock(productInfo, sourceHtml) {
  if (!productInfo || !sourceHtml) return;

  const liveOneTimeCard = productInfo.querySelector('[data-missence-non-subscription-card]');
  const nextOneTimeCard = sourceHtml.querySelector('[data-missence-non-subscription-card]');
  if (liveOneTimeCard && nextOneTimeCard) {
    liveOneTimeCard.innerHTML = nextOneTimeCard.innerHTML;
  }

  const liveRoot = productInfo.querySelector('[data-missence-subscription-root]');
  const nextRoot = sourceHtml.querySelector('[data-missence-subscription-root]');

  if (!liveRoot || !nextRoot) return;

  const activeSubscriptionTrigger = liveRoot.querySelector('[data-missence-subscription-tab-trigger].is-active');
  const activeVariantTrigger = liveRoot.querySelector('[data-missence-variant-tab-trigger].is-active');
  const activeTabKey = activeVariantTrigger
    ? 'variant'
    : activeSubscriptionTrigger?.getAttribute('data-missence-subscription-tab-trigger') || 'subscription';
  const activeVariantId = activeVariantTrigger?.getAttribute('data-missence-variant-tab-trigger') || '';

  liveRoot.innerHTML = nextRoot.innerHTML;
  delete liveRoot.dataset.missenceSubscriptionsReady;
  initMissenceSubscriptions(liveRoot);

  if (activeTabKey === 'variant' && activeVariantId !== '') {
    syncMissenceTabState(liveRoot, 'variant', activeVariantId);
    setMissenceSubscriptionPanelState(liveRoot, 'one-time');
    return;
  }

  activateMissenceSubscriptionTab(liveRoot, activeTabKey);
}

function setMissenceSubscriptionPanelState(root, tabKey) {
  const requestedPanel = root.querySelector(`[data-missence-subscription-tab-panel="${tabKey}"]`);
  const fallbackPanel = root.querySelector('[data-missence-subscription-tab-panel="subscription"]');
  const targetPanel = requestedPanel || fallbackPanel;

  root.querySelectorAll('[data-missence-subscription-tab-panel]').forEach((panel) => {
    const isActive = panel === targetPanel;
    panel.classList.toggle('is-active', isActive);
    panel.hidden = !isActive;
  });

  const preferredType = tabKey === 'one-time' ? 'one-time' : 'subscription';
  const firstRadio =
    targetPanel?.querySelector(`.missence-pdp-main__subscription-radio[data-selling-plan-type="${preferredType}"]`) ||
    targetPanel?.querySelector('.missence-pdp-main__subscription-radio');
  if (firstRadio) firstRadio.checked = true;
  syncMissenceSellingPlan(root);
}

function activateMissenceSubscriptionTab(root, tabKey) {
  if (!root) return;

  if (tabKey === 'subscription') {
    const subscriptionVariantId = root.getAttribute('data-missence-subscription-variant-id');
    const productInfo = root.closest('product-info');
    const selectedVariantId = productInfo?.querySelector('input.product-variant-id')?.value;

    if (
      subscriptionVariantId &&
      productInfo &&
      selectedVariantId &&
      String(selectedVariantId) !== String(subscriptionVariantId)
    ) {
      syncMissenceTabState(root, tabKey);
      setMissenceSubscriptionPanelState(root, tabKey);
      selectMissenceVariant(productInfo, subscriptionVariantId);
      return;
    }
  }

  syncMissenceTabState(root, tabKey);
  setMissenceSubscriptionPanelState(root, tabKey);
}

function initMissenceSubscriptions(root) {
  if (!root || root.dataset.missenceSubscriptionsReady === 'true') return;
  root.dataset.missenceSubscriptionsReady = 'true';
  syncMissenceSellingPlan(root);
}

function selectMissenceVariant(productInfo, variantId) {
  if (!variantId) return;

  const variantSelects = productInfo?.querySelector('variant-selects');
  if (!variantSelects) return null;
  const targetVariantData = productInfo?.querySelector(`[data-missence-variant-tab-json="${variantId}"]`)?.textContent;
  const targetVariant = targetVariantData ? JSON.parse(targetVariantData) : null;
  if (!targetVariant) return null;

  const optionValues = targetVariant.options || [];
  const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
  const nextInputs = [];

  fieldsets.forEach((fieldset, index) => {
    const optionValue = optionValues[index];
    if (typeof optionValue !== 'string') return;
    const nextInput = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((input) => input.value === optionValue);
    if (nextInput) {
      nextInput.checked = true;
      nextInputs.push(nextInput);
    }
  });

  const lastInput = nextInputs[nextInputs.length - 1];
  if (lastInput) {
    lastInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  return { targetVariant, lastInput };
}

function activateMissenceVariantTab(trigger) {
  const variantId = trigger?.getAttribute('data-missence-variant-tab-trigger');
  if (!variantId) return;

  const root = trigger.closest('[data-missence-subscription-root]');
  const productInfo = trigger.closest('product-info');
  if (root) {
    syncMissenceTabState(root, 'variant', variantId);
    setMissenceSubscriptionPanelState(root, 'one-time');
  }
  selectMissenceVariant(productInfo, variantId);
}

let pendingMissenceCartUpsell = null;
let pendingMissenceAddSequence = null;
const MISSENCE_CART_UPSELL_ANIMATION_MS = 380;
const MISSENCE_EXPERIENCE_VIDEO_FADE_MS = 550;
const MISSENCE_EXPERIENCE_GROUP_PROPERTY = '_missence_experience_group';
const MISSENCE_EXPERIENCE_ROLE_PROPERTY = '_missence_experience_role';

function createMissenceExperienceGroupId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();

  return `missence-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function setMissencePrivateLineProperty(form, propertyName, value) {
  let input = form.querySelector(`input[name="properties[${propertyName}]"]`);
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${propertyName}]`;
    form.appendChild(input);
  }
  input.value = value;
}

function prepareMissenceExperienceParent(event) {
  const form = event.target;
  const productForm = form?.closest?.('product-form');
  const popup = productForm?.closest('product-info')?.querySelector('[data-missence-cart-upsell]');
  if (!productForm || !popup) return;

  const groupId = createMissenceExperienceGroupId();
  popup.__missenceExperienceGroupId = groupId;
  setMissencePrivateLineProperty(form, MISSENCE_EXPERIENCE_GROUP_PROPERTY, groupId);
  setMissencePrivateLineProperty(form, MISSENCE_EXPERIENCE_ROLE_PROPERTY,
    popup.getAttribute('data-remove-with-parent') === 'true' ? 'parent' : '');
}

function getMissenceCartUpsellTagConfig(popup) {
  const configElement = popup?.querySelector('[data-missence-cart-upsell-tag-config]');
  if (!configElement?.textContent) return null;

  try {
    return JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('Invalid Missence popup tag configuration.', error);
    return null;
  }
}

function getMissenceSubscriptionContext(popup) {
  const section = popup?.closest('.shopify-section') || document;
  const checkedOption = section.querySelector(
    '[data-missence-subscription-root] .missence-pdp-main__subscription-radio:checked'
  );
  const isSubscription =
    checkedOption?.dataset.sellingPlanType === 'subscription' && Boolean(checkedOption.value);

  return {
    isSubscription,
    cadenceDays: Math.max(0, Number.parseInt(checkedOption?.dataset.cadenceDays || '0', 10) || 0),
  };
}

function resolveMissenceCartUpsellTag(config, cadenceDays) {
  if (!config?.enabled) return '';

  const rules = String(config.rules || '').split(/\r?\n/);
  for (const rule of rules) {
    const separatorIndex = rule.indexOf('::');
    if (separatorIndex < 0) continue;

    const interval = rule.slice(0, separatorIndex).trim();
    const tag = rule.slice(separatorIndex + 2).trim();
    if (!interval || !tag) continue;

    const rangeMatch = interval.match(/^(\d+)\s*-\s*(\d+)$/);
    if (rangeMatch) {
      const minimum = Number.parseInt(rangeMatch[1], 10);
      const maximum = Number.parseInt(rangeMatch[2], 10);
      if (cadenceDays >= Math.min(minimum, maximum) && cadenceDays <= Math.max(minimum, maximum)) return tag;
      continue;
    }

    if (Number.parseInt(interval, 10) === cadenceDays && /^\d+$/.test(interval)) return tag;
  }

  return String(config.defaultTag || '').trim();
}

function initMissenceCartUpsells(scope = document) {
  scope.querySelectorAll('[data-missence-cart-upsell]').forEach((popup) => {
    popup.hidden = true;
    popup.classList.remove('is-active', 'is-closing', 'is-loading');
    popup.__missenceSequenceStage = null;
    if (popup.__missenceSequenceTimer) {
      window.clearTimeout(popup.__missenceSequenceTimer);
      popup.__missenceSequenceTimer = null;
    }
  });

  scope.querySelectorAll('[data-missence-experience-video]').forEach((overlay) => {
    overlay.hidden = true;
    overlay.classList.remove('is-active', 'is-closing');
    overlay.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.currentTime = 0;
    });
    overlay.__missenceCartReady = false;
    overlay.__missenceVideoFinished = false;
    overlay.__missenceSequenceCompleted = false;
  });
}

function hideMissenceExperienceVideo(overlay, onHidden) {
  if (!overlay) return;
  if (typeof onHidden === 'function') overlay.__missenceAfterHide = onHidden;
  if (overlay.hidden) {
    const callback = overlay.__missenceAfterHide;
    overlay.__missenceAfterHide = null;
    if (callback) callback();
    return;
  }
  if (overlay.classList.contains('is-closing')) return;

  window.clearTimeout(overlay.__missenceHideTimer);
  overlay.classList.add('is-closing');
  overlay.classList.remove('is-active');

  overlay.__missenceHideTimer = window.setTimeout(() => {
    overlay.querySelectorAll('video').forEach((video) => video.pause());
    overlay.hidden = true;
    overlay.classList.remove('is-closing');
    overlay.__missenceHideTimer = null;
    const callback = overlay.__missenceAfterHide;
    overlay.__missenceAfterHide = null;
    if (callback) callback();
  }, MISSENCE_EXPERIENCE_VIDEO_FADE_MS);
}

function completeMissenceExperienceVideoSequence(overlay) {
  if (
    !overlay ||
    overlay.__missenceSequenceCompleted ||
    !overlay.__missenceVideoFinished ||
    !overlay.__missenceCartReady
  ) {
    return;
  }

  overlay.__missenceSequenceCompleted = true;
  const afterComplete = overlay.__missenceAfterComplete;
  overlay.__missenceAfterComplete = null;

  if (afterComplete) {
    hideMissenceExperienceVideo(overlay, afterComplete);
    return;
  }

  const popup = overlay.__missencePopup;
  clearMissenceCartUpsellSequenceTimer(popup);
  if (pendingMissenceCartUpsell === popup) pendingMissenceCartUpsell = null;
  if (popup) {
    popup.__missenceSequenceStage = null;
    hideMissenceCartUpsell(popup);
  }

  hideMissenceExperienceVideo(overlay, () => {
    if (overlay.__missenceRedirectUrl) {
      window.location.href = overlay.__missenceRedirectUrl;
      return;
    }
    if (window.MissenceCart && typeof window.MissenceCart.open === 'function') {
      window.MissenceCart.open();
      return;
    }
    document.querySelector('cart-drawer')?.open();
  });
}

function finishMissenceExperienceVideo(overlay) {
  if (!overlay) return;
  overlay.__missenceVideoFinished = true;
  completeMissenceExperienceVideoSequence(overlay);
}

function showMissenceExperienceVideo(popup, overlayOverride, onComplete) {
  const blockId = popup?.getAttribute('data-block-id');
  const overlay = overlayOverride || (blockId
    ? document.querySelector(`[data-missence-experience-video][data-block-id="${CSS.escape(blockId)}"]`)
    : null);
  if (!overlay) return false;

  // Portaling the overlay to body keeps it outside the product and drawer stacking contexts.
  if (overlay.parentElement !== document.body) document.body.appendChild(overlay);

  document.querySelectorAll('[data-missence-experience-video]:not([hidden])').forEach((activeOverlay) => {
    if (activeOverlay !== overlay) hideMissenceExperienceVideo(activeOverlay);
  });

  window.clearTimeout(overlay.__missenceHideTimer);
  const videos = Array.from(overlay.querySelectorAll('video'));
  videos.forEach((video) => {
    video.pause();
    video.currentTime = 0;
  });

  const activeVideo = videos.find((video) => window.getComputedStyle(video).display !== 'none');
  if (!activeVideo) return false;

  overlay.__missencePopup = popup;
  overlay.__missenceCartReady = Boolean(onComplete);
  overlay.__missenceVideoFinished = false;
  overlay.__missenceSequenceCompleted = false;
  overlay.__missenceRedirectUrl = '';
  overlay.__missenceAfterComplete = typeof onComplete === 'function' ? onComplete : null;

  const finish = () => finishMissenceExperienceVideo(overlay);
  activeVideo.addEventListener('ended', finish, { once: true });
  activeVideo.addEventListener('error', finish, { once: true });

  overlay.hidden = false;
  overlay.classList.remove('is-active', 'is-closing');
  overlay.getBoundingClientRect();
  window.requestAnimationFrame(() => overlay.classList.add('is-active'));

  const playPromise = activeVideo.play();
  if (playPromise && typeof playPromise.catch === 'function') {
    playPromise.catch(finish);
  }

  return overlay;
}

function isMissenceCartDrawerOpen() {
  const drawer = document.querySelector('cart-drawer');
  return Boolean(drawer?.classList.contains('active') || document.body.classList.contains('cart-drawer-open'));
}

function getMissenceCartUpsellConfig(popup) {
  const delaySeconds = Number.parseFloat(popup?.getAttribute('data-opening-delay') || '3');

  return {
    order: popup?.getAttribute('data-opening-order') || 'cart_before_popup',
    mode: popup?.getAttribute('data-opening-mode') || 'action',
    delayMs: Math.max(1, Number.isFinite(delaySeconds) ? delaySeconds : 3) * 1000,
  };
}

function getMissenceExperienceVideoForProduct() {
  const productId = document.querySelector('product-info[data-product-id]')?.getAttribute('data-product-id');
  if (productId) {
    const match = document.querySelector(
      `[data-missence-experience-video][data-product-id="${CSS.escape(productId)}"]`
    );
    if (match) return filterMissenceExperienceVideoByUpsell(match);
  }
  return filterMissenceExperienceVideoByUpsell(
    document.querySelector('[data-missence-experience-video][data-placement]')
  );
}

function filterMissenceExperienceVideoByUpsell(overlay) {
  if (overlay?.getAttribute('data-require-upsell') !== 'true') return overlay;
  const productId = overlay.getAttribute('data-product-id');
  const productInfo = productId
    ? document.querySelector(`product-info[data-product-id="${CSS.escape(productId)}"]`)
    : document.querySelector('product-info');
  return productInfo?.querySelector('[data-missence-cart-upsell]') ? overlay : null;
}

function buildMissenceAddSequence(popup, overlay) {
  const { order, mode } = getMissenceCartUpsellConfig(popup);
  const items = popup
    ? (order === 'popup_before_cart' ? ['popup', 'cart'] : ['cart', 'popup'])
    : ['cart'];

  if (overlay) {
    const placement = overlay.getAttribute('data-placement') || 'before_cart';
    const usesPopupCta = overlay.getAttribute('data-use-popup-cta') === 'true' && Boolean(popup);

    if (!usesPopupCta && placement !== 'after_experience_add' && placement !== 'skip') {
      const anchorIndex = items.indexOf('cart');
      if (anchorIndex >= 0) {
        items.splice(anchorIndex + (placement.startsWith('after_') ? 1 : 0), 0, 'video');
      }
    }
  }

  return {
    popup,
    overlay,
    items,
    index: 0,
    mode,
    started: false,
    awaitingPopupAdd: false,
  };
}

function getMissenceSequenceItem(sequence = pendingMissenceAddSequence) {
  return sequence?.items?.[sequence.index] || null;
}

function closeMissenceSequenceItem(sequence, item) {
  if (item === 'popup') hideMissenceCartUpsell(sequence.popup);
  if (item === 'cart' && isMissenceCartDrawerOpen()) {
    if (window.MissenceCart && typeof window.MissenceCart.close === 'function') {
      window.MissenceCart.close();
    } else {
      document.querySelector('cart-drawer')?.close();
    }
  }
}

async function runMissenceAddSequence(sequence = pendingMissenceAddSequence) {
  if (!sequence || sequence !== pendingMissenceAddSequence) return;
  const item = getMissenceSequenceItem(sequence);
  if (!item) {
    pendingMissenceAddSequence = null;
    pendingMissenceCartUpsell = null;
    return;
  }

  sequence.started = true;
  if (item === 'video') {
    if (sequence.overlay?.getAttribute('data-require-upsell') === 'true') {
      let hasParent = false;
      try {
        if (sequence.popup) {
          const cart = await getMissenceUpsellCart();
          hasParent = await hasMissenceCartUpsellParent(sequence.popup, cart) &&
            getMissenceCartUpsellRemainingQuantity(sequence.popup, cart) > 0;
        }
      } catch (error) {
        console.error(error);
      }
      if (sequence !== pendingMissenceAddSequence || getMissenceSequenceItem(sequence) !== 'video') return;
      if (!hasParent) {
        advanceMissenceAddSequence(sequence);
        return;
      }
    }
    if (!showMissenceExperienceVideo(sequence.popup, sequence.overlay, () => advanceMissenceAddSequence(sequence))) {
      advanceMissenceAddSequence(sequence);
    }
    return;
  }

  if (item === 'popup') {
    pendingMissenceCartUpsell = sequence.popup;
    sequence.popup.__missenceSequenceStage = 'popup_sequence';
    showMissenceCartUpsell(sequence.popup);
    if (sequence.mode === 'delay') {
      scheduleMissenceCartUpsellSequence(sequence.popup, () => advanceMissenceAddSequence(sequence));
    }
    return;
  }

  sequence.popup && (sequence.popup.__missenceSequenceStage = 'cart_sequence');
  if (!isMissenceCartDrawerOpen()) {
    window.setTimeout(() => {
      if (sequence !== pendingMissenceAddSequence || getMissenceSequenceItem(sequence) !== 'cart') return;
      if (window.MissenceCart && typeof window.MissenceCart.open === 'function') {
        window.MissenceCart.open();
      } else {
        document.querySelector('cart-drawer')?.open();
      }
    }, MISSENCE_CART_UPSELL_ANIMATION_MS);
  }
}

function advanceMissenceAddSequence(sequence = pendingMissenceAddSequence) {
  if (!sequence || sequence !== pendingMissenceAddSequence) return;
  const currentItem = getMissenceSequenceItem(sequence);
  clearMissenceCartUpsellSequenceTimer(sequence.popup);
  closeMissenceSequenceItem(sequence, currentItem);
  sequence.index += 1;
  sequence.started = false;

  const delay = currentItem === 'video' ? 0 : MISSENCE_CART_UPSELL_ANIMATION_MS;
  window.setTimeout(() => runMissenceAddSequence(sequence), delay);
}

function queueMissenceConfiguredAddSequence(popup) {
  const overlay = getMissenceExperienceVideoForProduct();
  const sequence = buildMissenceAddSequence(popup, overlay);
  pendingMissenceAddSequence = sequence;
  pendingMissenceCartUpsell = popup || null;
  if (popup) popup.__missenceSubscriptionContext = getMissenceSubscriptionContext(popup);

  const firstItem = getMissenceSequenceItem(sequence);
  if (firstItem !== 'cart') window.__missenceSuppressNextCartDrawer = true;
  if (firstItem === 'cart') {
    sequence.started = true;
    if (popup) popup.__missenceSequenceStage = 'cart_sequence';
  } else {
    runMissenceAddSequence(sequence);
  }
}

function clearMissenceCartUpsellSequenceTimer(popup) {
  if (!popup?.__missenceSequenceTimer) return;
  window.clearTimeout(popup.__missenceSequenceTimer);
  popup.__missenceSequenceTimer = null;
}

function scheduleMissenceCartUpsellSequence(popup, callback) {
  clearMissenceCartUpsellSequenceTimer(popup);
  const { delayMs } = getMissenceCartUpsellConfig(popup);
  popup.__missenceSequenceTimer = window.setTimeout(() => {
    popup.__missenceSequenceTimer = null;
    callback();
  }, delayMs);
}

function openMissenceCartAfterUpsell(popup) {
  if (!popup) return;

  clearMissenceCartUpsellSequenceTimer(popup);
  if (pendingMissenceCartUpsell === popup) pendingMissenceCartUpsell = null;
  popup.__missenceSequenceStage = null;
  hideMissenceCartUpsell(popup);

  window.setTimeout(() => {
    if (window.MissenceCart && typeof window.MissenceCart.open === 'function') {
      window.MissenceCart.open();
      return;
    }
    document.querySelector('cart-drawer')?.open();
  }, MISSENCE_CART_UPSELL_ANIMATION_MS);
}

function showMissenceUpsellAfterCart(popup) {
  if (!popup) return;

  clearMissenceCartUpsellSequenceTimer(popup);
  if (pendingMissenceCartUpsell === popup) pendingMissenceCartUpsell = null;
  popup.__missenceSequenceStage = 'popup_second';

  if (isMissenceCartDrawerOpen()) {
    if (window.MissenceCart && typeof window.MissenceCart.close === 'function') {
      window.MissenceCart.close();
    } else {
      document.querySelector('cart-drawer')?.close();
    }
  }

  window.setTimeout(() => showMissenceCartUpsell(popup), 120);
}

function queueMissenceCartUpsell(popup) {
  if (!popup) return;

  const { order } = getMissenceCartUpsellConfig(popup);
  clearMissenceCartUpsellSequenceTimer(popup);
  popup.__missenceSubscriptionContext = getMissenceSubscriptionContext(popup);
  pendingMissenceCartUpsell = popup;
  hideMissenceCartUpsell(popup);

  if (order === 'popup_before_cart') {
    window.__missenceSuppressNextCartDrawer = true;
    popup.__missenceSequenceStage = 'popup_first';

    window.setTimeout(() => {
      if (pendingMissenceCartUpsell !== popup || !popup.hidden || isMissenceCartDrawerOpen()) return;
      showMissenceCartUpsell(popup);
    }, 500);
    return;
  }

  popup.__missenceSequenceStage = 'cart_first';
}

function dismissMissenceUnavailableCartUpsell(popup) {
  clearMissenceCartUpsellSequenceTimer(popup);
  if (pendingMissenceCartUpsell === popup) pendingMissenceCartUpsell = null;
  hideMissenceCartUpsell(popup);
  if (pendingMissenceAddSequence?.popup === popup && getMissenceSequenceItem() === 'popup') {
    advanceMissenceAddSequence(pendingMissenceAddSequence);
  }
}

async function getMissenceUpsellCart() {
  const response = await fetch(`${window.routes.cart_url}.js`, { cache: 'no-store' });
  if (!response.ok) throw new Error('Unable to validate the Missence upsell cart.');
  return response.json();
}

function syncMissenceCartUpsellButton(popup, cart) {
  const button = popup.querySelector('[data-missence-cart-upsell-add]');
  if (!button) return;
  button.disabled = popup.getAttribute('data-upsell-available') === 'false' ||
    getMissenceCartUpsellRemainingQuantity(popup, cart) === 0;
  if (button.disabled) button.setAttribute('aria-disabled', 'true');
  else if (!popup.classList.contains('is-loading')) button.removeAttribute('aria-disabled');
}

function getMissenceCartUpsellRemainingQuantity(popup, cart) {
  const maximum = Math.max(1, Number.parseInt(popup.getAttribute('data-upsell-quantity') || '1', 10) || 1);
  const productId = popup.getAttribute('data-upsell-product-id');
  const variantId = popup.getAttribute('data-variant-id');
  const groupId = String(popup.__missenceExperienceGroupId || '').trim();
  if (!groupId) return 0;
  const existingQuantity = cart.items.reduce((total, item) => {
    const matchesProduct = productId
      ? String(item.product_id) === productId
      : String(item.variant_id || item.id) === variantId;
    const matches = matchesProduct && item.properties?.[MISSENCE_EXPERIENCE_GROUP_PROPERTY] === groupId;
    return total + (matches ? Math.max(0, Number(item.quantity) || 0) : 0);
  }, 0);
  return Math.max(0, maximum - existingQuantity);
}

async function hasMissenceCartUpsellParent(popup, cart) {
  if (popup.getAttribute('data-remove-with-parent') !== 'true') return true;
  const groupId = String(popup.__missenceExperienceGroupId || '').trim();
  if (!groupId) return false;
  if (!cart) {
    cart = await getMissenceUpsellCart();
  }
  return groupId === popup.__missenceExperienceGroupId && cart.items?.some((item) =>
    item.quantity > 0 &&
    item.properties?.[MISSENCE_EXPERIENCE_GROUP_PROPERTY] === groupId &&
    item.properties?.[MISSENCE_EXPERIENCE_ROLE_PROPERTY] === 'parent'
  );
}

async function showMissenceCartUpsell(popup) {
  if (!popup) return;

  try {
    const cart = await getMissenceUpsellCart();
    syncMissenceCartUpsellButton(popup, cart);
    if (!await hasMissenceCartUpsellParent(popup, cart) || !getMissenceCartUpsellRemainingQuantity(popup, cart)) {
      dismissMissenceUnavailableCartUpsell(popup);
      return;
    }
  } catch (error) {
    console.error(error);
    dismissMissenceUnavailableCartUpsell(popup);
    return;
  }

  document.querySelectorAll('[data-missence-cart-upsell].is-active').forEach((activePopup) => {
    if (activePopup !== popup) hideMissenceCartUpsell(activePopup);
  });

  if (popup.__missenceHideTimer) {
    window.clearTimeout(popup.__missenceHideTimer);
    popup.__missenceHideTimer = null;
  }

  popup.hidden = false;
  popup.classList.remove('is-active', 'is-closing', 'is-loading');
  popup.getBoundingClientRect();

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      popup.classList.add('is-active');

      const { mode } = getMissenceCartUpsellConfig(popup);
      if (popup.__missenceSequenceStage === 'popup_first' && mode === 'delay') {
        scheduleMissenceCartUpsellSequence(popup, () => openMissenceCartAfterUpsell(popup));
      }
    });
  });
}

function hideMissenceCartUpsell(popup) {
  if (!popup) return;

  if (popup.hidden) return;

  if (popup.__missenceHideTimer) {
    window.clearTimeout(popup.__missenceHideTimer);
    popup.__missenceHideTimer = null;
  }

  const finishClose = () => {
    if (popup.classList.contains('is-active')) return;
    popup.hidden = true;
    popup.classList.remove('is-closing', 'is-loading');
    popup.removeEventListener('transitionend', handleTransitionEnd);
    if (popup.__missenceHideTimer) {
      window.clearTimeout(popup.__missenceHideTimer);
      popup.__missenceHideTimer = null;
    }
  };

  const handleTransitionEnd = (event) => {
    if (event.target !== popup || event.propertyName !== 'transform') return;
    finishClose();
  };

  popup.addEventListener('transitionend', handleTransitionEnd);
  popup.classList.add('is-closing');
  popup.classList.remove('is-active', 'is-loading');
  popup.__missenceHideTimer = window.setTimeout(finishClose, MISSENCE_CART_UPSELL_ANIMATION_MS);
}

async function addMissenceCartUpsell(button) {
  const popup = button?.closest('[data-missence-cart-upsell]');
  const variantId = popup?.getAttribute('data-variant-id');
  let upsellQuantity;
  if (!popup || !variantId || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

  popup.classList.add('is-loading');
  button.setAttribute('aria-disabled', 'true');
  try {
    const cart = await getMissenceUpsellCart();
    upsellQuantity = getMissenceCartUpsellRemainingQuantity(popup, cart);
    syncMissenceCartUpsellButton(popup, cart);
    if (!await hasMissenceCartUpsellParent(popup, cart) || !upsellQuantity) {
      button.removeAttribute('aria-disabled');
      dismissMissenceUnavailableCartUpsell(popup);
      return;
    }
  } catch (error) {
    console.error(error);
    popup.classList.remove('is-loading');
    button.removeAttribute('aria-disabled');
    return;
  }
  const configuredOverlay = getMissenceExperienceVideoForProduct();
  const useLegacyVideo =
    configuredOverlay?.getAttribute('data-use-popup-cta') === 'true' ||
    configuredOverlay?.getAttribute('data-placement') === 'after_experience_add';
  const experienceOverlay = useLegacyVideo ? showMissenceExperienceVideo(popup, configuredOverlay) : false;
  popup.__missenceExperienceOverlay = experienceOverlay || null;

  const activeSequence = pendingMissenceAddSequence;
  if (activeSequence && activeSequence.popup === popup && getMissenceSequenceItem(activeSequence) === 'popup') {
    activeSequence.awaitingPopupAdd = true;
    if (experienceOverlay && activeSequence.index + 1 >= activeSequence.items.length) {
      activeSequence.items.push('cart');
    }
    const hasNextSequenceItem = activeSequence.index + 1 < activeSequence.items.length;
    if (hasNextSequenceItem || experienceOverlay) window.__missenceSuppressNextCartDrawer = true;
    if (experienceOverlay) {
      experienceOverlay.__missenceAfterComplete = () => {
        activeSequence.awaitingPopupAdd = false;
        advanceMissenceAddSequence(activeSequence);
      };
    }
  }

  const subscriptionContext = popup.__missenceSubscriptionContext || getMissenceSubscriptionContext(popup);
  const tagConfig = getMissenceCartUpsellTagConfig(popup);
  const comboTag = subscriptionContext.isSubscription
    ? resolveMissenceCartUpsellTag(tagConfig, subscriptionContext.cadenceDays)
    : '';
  const comboPropertyName = String(tagConfig?.propertyName || '').trim();
  const comboAttributeName = String(tagConfig?.attributeName || '').trim();
  const experienceGroupId = String(popup.__missenceExperienceGroupId || '').trim();

  const { mode } = getMissenceCartUpsellConfig(popup);
  if (!activeSequence) {
    if (popup.__missenceSequenceStage === 'popup_first' && mode === 'delay') {
      window.__missenceSuppressNextCartDrawer = true;
    } else {
      clearMissenceCartUpsellSequenceTimer(popup);
      if (pendingMissenceCartUpsell === popup) pendingMissenceCartUpsell = null;
      popup.__missenceSequenceStage = null;
    }
  }
  if (experienceOverlay) {
    clearMissenceCartUpsellSequenceTimer(popup);
    window.__missenceSuppressNextCartDrawer = true;
  }

  if (window.MissenceCart && typeof window.MissenceCart.add === 'function') {
    const addDetail = {
      id: variantId,
      quantity: upsellQuantity,
      source: 'missence-pdp-cart-upsell',
      triggeredBy: button,
    };
    if (comboTag && comboPropertyName) addDetail[`properties[${comboPropertyName}]`] = comboTag;
    if (experienceGroupId) {
      addDetail[`properties[${MISSENCE_EXPERIENCE_GROUP_PROPERTY}]`] = experienceGroupId;
      if (popup.getAttribute('data-remove-with-parent') === 'true') {
        addDetail[`properties[${MISSENCE_EXPERIENCE_ROLE_PROPERTY}]`] = 'upsell';
      }
    }
    if (comboTag && comboAttributeName) addDetail.cartAttributes = { [comboAttributeName]: comboTag };
    window.MissenceCart.add(addDetail);
    return;
  }

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', String(upsellQuantity));
  if (comboTag && comboPropertyName) formData.append(`properties[${comboPropertyName}]`, comboTag);
  if (experienceGroupId) {
    formData.append(`properties[${MISSENCE_EXPERIENCE_GROUP_PROPERTY}]`, experienceGroupId);
    if (popup.getAttribute('data-remove-with-parent') === 'true') {
      formData.append(`properties[${MISSENCE_EXPERIENCE_ROLE_PROPERTY}]`, 'upsell');
    }
  }

  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];
  config.body = formData;

  fetch(window.routes.cart_add_url, config)
    .then((response) => response.json())
    .then(async (response) => {
      if (!response.status && comboTag && comboAttributeName && window.routes?.cart_update_url) {
        try {
          await fetch(window.routes.cart_update_url, {
            ...fetchConfig(),
            body: JSON.stringify({ attributes: { [comboAttributeName]: comboTag } }),
          });
        } catch (error) {
          console.error(error);
        }
      }
      return response;
    })
    .then((response) => {
      if (response.status) throw response;
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      hideMissenceCartUpsell(popup);
      if (experienceOverlay) {
        experienceOverlay.__missenceCartReady = true;
        experienceOverlay.__missenceRedirectUrl = window.routes?.cart_url || '';
        completeMissenceExperienceVideoSequence(experienceOverlay);
      } else if (activeSequence) {
        activeSequence.awaitingPopupAdd = false;
        advanceMissenceAddSequence(activeSequence);
      } else if (window.routes?.cart_url) {
        window.location.href = window.routes.cart_url;
      }
    })
    .catch((error) => {
      console.error(error);
      if (experienceOverlay) hideMissenceExperienceVideo(experienceOverlay);
      window.__missenceSuppressNextCartDrawer = false;
      popup.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    });
}

if (!window.__missenceProductPageBound) {
  window.__missenceProductPageBound = true;

  document.addEventListener('submit', prepareMissenceExperienceParent, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('[data-missence-gallery]').forEach(initMissenceProductGallery);
      document.querySelectorAll('[data-missence-subscription-root]').forEach(initMissenceSubscriptions);
      initMissenceCartUpsells();
      initMissenceClaims();
      requestAnimationFrame(clearPendingProductSections);
    });
  } else {
    document.querySelectorAll('[data-missence-gallery]').forEach(initMissenceProductGallery);
    document.querySelectorAll('[data-missence-subscription-root]').forEach(initMissenceSubscriptions);
    initMissenceCartUpsells();
    initMissenceClaims();
    requestAnimationFrame(clearPendingProductSections);
  }

  window.addEventListener('load', clearPendingProductSections);

  document.addEventListener('shopify:section:load', (event) => initMissenceClaims(event.target));

  document.addEventListener('shopify:block:select', (event) => {
    const objective = event.target.closest('[data-missence-claims-objective]');
    const root = objective?.closest('[data-missence-claims]');
    const select = root?.querySelector('[data-missence-claims-objective-select]');
    if (!select || root.dataset.missenceClaimsReady !== 'true') return;
    select.value = objective.id;
    showMissenceClaimsObjective(root, objective.id);
  });

  document.addEventListener('click', (event) => {
  const upsellClose = event.target.closest('[data-missence-cart-upsell-close]');
  if (upsellClose) {
    const popup = upsellClose.closest('[data-missence-cart-upsell]');
    if (
      pendingMissenceAddSequence?.popup === popup &&
      getMissenceSequenceItem(pendingMissenceAddSequence) === 'popup'
    ) {
      advanceMissenceAddSequence(pendingMissenceAddSequence);
      return;
    }
    const { mode } = getMissenceCartUpsellConfig(popup);
    if (popup?.__missenceSequenceStage === 'popup_first' && mode === 'action') {
      openMissenceCartAfterUpsell(popup);
    } else {
      hideMissenceCartUpsell(popup);
    }
    return;
  }

  const upsellAdd = event.target.closest('[data-missence-cart-upsell-add]');
  if (upsellAdd) {
    addMissenceCartUpsell(upsellAdd);
    return;
  }

  const subscriptionTabTrigger = event.target.closest('[data-missence-subscription-tab-trigger]');

  if (subscriptionTabTrigger) {
    const root = subscriptionTabTrigger.closest('[data-missence-subscription-root]');
    if (!root) return;
    activateMissenceSubscriptionTab(root, subscriptionTabTrigger.getAttribute('data-missence-subscription-tab-trigger'));
    return;
  }

  const variantTabTrigger = event.target.closest('[data-missence-variant-tab-trigger]');

  if (variantTabTrigger) {
    activateMissenceVariantTab(variantTabTrigger);
    return;
  }

  const subscriptionCard = event.target.closest('[data-missence-subscription-card]');

  if (subscriptionCard) {
    const root = subscriptionCard.closest('[data-missence-subscription-root]');
    const radio = subscriptionCard.querySelector('.missence-pdp-main__subscription-radio');
    if (!root || !radio) return;

    // El radio y su <label> ya lo marcan de forma nativa y lanzan «change».
    if (event.target.closest('input, label')) return;

    if (!radio.checked) {
      radio.checked = true;
      syncMissenceSellingPlan(root);
      announceMissencePurchaseOption(root);
    }
    return;
  }

  const comparisonTrigger = event.target.closest('[data-missence-comparison-trigger]');

  if (comparisonTrigger) {
    const root = comparisonTrigger.closest('[data-missence-comparison]');
    if (!root) return;

    const targetId = comparisonTrigger.getAttribute('data-missence-comparison-trigger');
    syncPanels(root, targetId, 'comparison');
  }

  const ingredientTrigger = event.target.closest('[data-missence-ingredient-open]');

  if (ingredientTrigger) {
    const root = ingredientTrigger.closest('[data-missence-ingredients]');
    if (!root) return;

    openIngredientModal(root, ingredientTrigger.getAttribute('data-missence-ingredient-open'));
    return;
  }

  const ingredientClose = event.target.closest('[data-missence-ingredient-close]');

  if (ingredientClose) {
    closeIngredientModal(ingredientClose.closest('[data-missence-ingredient-modal]'));
    return;
  }

  const differencesPrev = event.target.closest('[data-missence-differences-prev]');

  if (differencesPrev && !differencesPrev.classList.contains('is-disabled')) {
    const root = differencesPrev.closest('[data-missence-differences]');
    if (!root) return;
    const current = Number(root.getAttribute('data-missence-differences-current') || 0);
    syncDifferences(root, current - 1);
    return;
  }

  const differencesNext = event.target.closest('[data-missence-differences-next]');

  if (differencesNext && !differencesNext.classList.contains('is-disabled')) {
    const root = differencesNext.closest('[data-missence-differences]');
    if (!root) return;
    const current = Number(root.getAttribute('data-missence-differences-current') || 0);
    syncDifferences(root, current + 1);
    return;
  }
  });

  document.addEventListener('change', (event) => {
  const claimsSelect = event.target.closest('[data-missence-claims-objective-select]');
  if (claimsSelect) {
    const root = claimsSelect.closest('[data-missence-claims]');
    if (root) showMissenceClaimsObjective(root, claimsSelect.value, { animate: true, announce: true });
    return;
  }

  const subscriptionOption = event.target.closest('.missence-pdp-main__subscription-radio');
  if (subscriptionOption) {
    const root = subscriptionOption.closest('[data-missence-subscription-root]');
    if (!root) return;
    syncMissenceSellingPlan(root);
    announceMissencePurchaseOption(root);
  }
  });

  document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('[data-missence-experience-video]:not([hidden])').forEach((overlay) => {
    finishMissenceExperienceVideo(overlay);
  });
  document.querySelectorAll('[data-missence-cart-upsell].is-active').forEach((popup) => {
    if (
      pendingMissenceAddSequence?.popup === popup &&
      getMissenceSequenceItem(pendingMissenceAddSequence) === 'popup'
    ) {
      advanceMissenceAddSequence(pendingMissenceAddSequence);
      return;
    }
    const { mode } = getMissenceCartUpsellConfig(popup);
    if (popup.__missenceSequenceStage === 'popup_first' && mode === 'action') {
      openMissenceCartAfterUpsell(popup);
    } else {
      hideMissenceCartUpsell(popup);
    }
  });
  document.querySelectorAll('[data-missence-ingredient-modal]:not([hidden])').forEach((modal) => {
    closeIngredientModal(modal);
  });
  });

  if (!window.__missenceProductVariantSubscriber && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    window.__missenceProductVariantSubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const sectionId = data?.sectionId;
      const featuredMediaId = data?.variant?.featured_media?.id;
      const productInfo = sectionId ? document.querySelector(`product-info[data-section="${sectionId}"]`) : null;
      if (!productInfo) return;

      syncMissencePurchaseBlock(productInfo, data?.html);

      if (!featuredMediaId) return;

      const root = productInfo.querySelector('[data-missence-gallery]');
      if (!root) return;

      const targetId = String(featuredMediaId);
      if (typeof root.__missenceScrollToMedia === 'function') {
        root.__missenceScrollToMedia(targetId);
      } else {
        setMissenceGalleryActiveState(root, targetId);
      }
    });
  }

  if (!window.__missenceProductCartUpsellSubscriber && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    window.__missenceProductCartUpsellSubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const detail = event || {};

      // Cart changes include the full cart; add responses can contain only the added lines.
      if (detail.source === 'cart-items' && Array.isArray(detail.cartData?.items)) {
        document.querySelectorAll('[data-missence-cart-upsell]').forEach(async (popup) => {
          syncMissenceCartUpsellButton(popup, detail.cartData);
          if (!await hasMissenceCartUpsellParent(popup, detail.cartData) ||
              !getMissenceCartUpsellRemainingQuantity(popup, detail.cartData)) {
            dismissMissenceUnavailableCartUpsell(popup);
          }
        });
      }

      if (detail.source === 'product-form') {
        const popup = document.querySelector('[data-missence-cart-upsell]');
        if (popup || getMissenceExperienceVideoForProduct()) {
          queueMissenceConfiguredAddSequence(popup);
        }
        return;
      }

      if (detail.source === 'missence-pdp-cart-upsell') {
        getMissenceUpsellCart().then((cart) => {
          document.querySelectorAll('[data-missence-cart-upsell]').forEach((popup) => {
            syncMissenceCartUpsellButton(popup, cart);
          });
        }).catch(console.error);
        document.querySelectorAll('[data-missence-cart-upsell]').forEach((popup) => {
          if (pendingMissenceAddSequence?.popup === popup) {
            popup.classList.remove('is-loading');
            popup.querySelector('[data-missence-cart-upsell-add]')?.removeAttribute('aria-disabled');
            return;
          }
          const { mode } = getMissenceCartUpsellConfig(popup);
          const waitsForDelay = popup.__missenceSequenceStage === 'popup_first' && mode === 'delay';
          if (!waitsForDelay) {
            clearMissenceCartUpsellSequenceTimer(popup);
            popup.__missenceSequenceStage = null;
            if (pendingMissenceCartUpsell === popup) pendingMissenceCartUpsell = null;
            hideMissenceCartUpsell(popup);
          }
          popup.classList.remove('is-loading');
          popup.querySelector('[data-missence-cart-upsell-add]')?.removeAttribute('aria-disabled');
        });
      }
    });
  }

  document.addEventListener('missence:cart:drawer-suppressed', () => {
    const popup = pendingMissenceCartUpsell;
    if (!popup || popup.__missenceSequenceStage !== 'popup_first' || !popup.hidden) return;
    window.setTimeout(() => showMissenceCartUpsell(popup), 80);
  });

  document.addEventListener('missence:cart:drawer-opened', () => {
    if (getMissenceSequenceItem() === 'cart') {
      if (pendingMissenceAddSequence.mode === 'delay') {
        scheduleMissenceCartUpsellSequence(
          pendingMissenceAddSequence.popup,
          () => advanceMissenceAddSequence(pendingMissenceAddSequence)
        );
      }
      return;
    }
    const popup = pendingMissenceCartUpsell;
    if (!popup || popup.__missenceSequenceStage !== 'cart_first') return;

    const { mode } = getMissenceCartUpsellConfig(popup);
    if (mode === 'delay') {
      scheduleMissenceCartUpsellSequence(popup, () => showMissenceUpsellAfterCart(popup));
    }
  });

  document.addEventListener('missence:cart:drawer-closed', () => {
    if (getMissenceSequenceItem() === 'cart') {
      if (pendingMissenceAddSequence.mode === 'action') {
        advanceMissenceAddSequence(pendingMissenceAddSequence);
      }
      return;
    }
    const popup = pendingMissenceCartUpsell;
    if (!popup || popup.__missenceSequenceStage !== 'cart_first') return;

    const { mode } = getMissenceCartUpsellConfig(popup);
    if (mode === 'action') {
      showMissenceUpsellAfterCart(popup);
    }
  });

  document.addEventListener('missence:cart:error', (event) => {
    if (event.detail?.source !== 'missence-pdp-cart-upsell') return;

    document.querySelectorAll('[data-missence-cart-upsell]').forEach((popup) => {
      const overlay = popup.__missenceExperienceOverlay;
      if (overlay) hideMissenceExperienceVideo(overlay);
      popup.__missenceExperienceOverlay = null;
      popup.classList.remove('is-loading');
      popup.querySelector('[data-missence-cart-upsell-add]')?.removeAttribute('aria-disabled');
    });
    if (pendingMissenceAddSequence?.popup) pendingMissenceAddSequence = null;
    pendingMissenceCartUpsell = null;
    window.__missenceSuppressNextCartDrawer = false;
  });

  document.addEventListener('missence:cart:added', (event) => {
    if (event.detail?.source !== 'missence-pdp-cart-upsell') return;

    const variantId = String(event.detail?.productVariantId || '');
    const popup = Array.from(document.querySelectorAll('[data-missence-cart-upsell]')).find(
      (candidate) => candidate.getAttribute('data-variant-id') === variantId
    );
    const overlay = popup?.__missenceExperienceOverlay;
    if (overlay) {
      overlay.__missenceCartReady = true;
      completeMissenceExperienceVideoSequence(overlay);
    } else if (
      pendingMissenceAddSequence?.popup === popup &&
      pendingMissenceAddSequence.awaitingPopupAdd
    ) {
      pendingMissenceAddSequence.awaitingPopupAdd = false;
      advanceMissenceAddSequence(pendingMissenceAddSequence);
    }
  });

  document.querySelectorAll('[data-missence-differences]').forEach((root) => {
    syncDifferences(root, Number(root.getAttribute('data-missence-differences-current') || 0));
  });
}


function syncPanels(root, targetId, namespace) {
  root.querySelectorAll(`[data-missence-${namespace}-trigger]`).forEach((trigger) => {
    trigger.classList.toggle('is-active', trigger.getAttribute(`data-missence-${namespace}-trigger`) === targetId);
  });

  root.querySelectorAll(`[data-missence-${namespace}-panel]`).forEach((panel) => {
    panel.classList.toggle('is-active', panel.getAttribute(`data-missence-${namespace}-panel`) === targetId);
  });
}
